<script setup lang="ts">
import { AgTableClient } from '@/modules/common/components/ag-table';
import i18n from '@/localisation/i18n';
import { logger } from '@/modules/common/services/logger.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { Agreement, AgreementStatus, Data, agreementsService } from '@/connect';
import * as cols from '@/modules/common/components/ag-table/columns/agreements';
import { computed, onMounted, shallowRef, watchEffect } from 'vue';
import { useIsIncoming } from '@/modules/agreements/composables';
import { Permission, useHasPermission } from '@/composables/user-permissions';
import { useAuthStore } from '@/store/auth-store';
import { useSocketStore } from '@/store/socket-store';

/* eslint-disable @typescript-eslint/unified-signatures */
const emit = defineEmits<{
  (event: 'view', agreement: Data<Agreement>): void;
  (event: 'duplicate', agreement: Data<Agreement>): void;
  (event: 'edit', agreement: Data<Agreement>): void;
  (event: 'close', agreement: Data<Agreement>): void;
  (event: 'review', agreement: Data<Agreement>): void;
  (event: 'update:reserved-short-names', reservedShortNames: Set<string>): void;
}>();
/* eslint-enable @typescript-eslint/unified-signatures */

const authStore = useAuthStore();

const socketStore = useSocketStore();
socketStore.useEventHandler('refreshAgreements', () => refreshData());

const canCreateOrEditAgreement = useHasPermission(
  Permission.AGREEMENT_EDIT,
  Permission.AGREEMENT_CREATE
);

const isBrokerAdmin = computed(() => {
  return (
    authStore.user?.roles?.includes('broker-admin') ||
    authStore.user?.roles?.includes('broker-user') ||
    false
  );
});

const props = withDefaults(
  defineProps<{
    companyIdFilter?: string;
  }>(),
  {
    companyIdFilter: undefined,
  }
);

const columnDefs = computed(() => [
  cols.id({ filter: true }),
  cols.status({ filter: true }),
  cols.side({ filter: true }),
  cols.counterpartyId({ filter: true }),
  cols.shortName({ filter: true }),
  cols.bookingAccount({ filter: true }),
  cols.counterpartyBookingAccount({ filter: true }),
  cols.tradeType({ filter: true }),
  cols.settlementType({ filter: true }),
  cols.percentCollateral({ filter: true }),
  cols.roundingRule({ filter: true }),
  cols.createdBy({ filter: true }),
  cols.approvedBy({ filter: true }),
  cols.createdAt({ filter: true }),
  cols.approvedAt({ filter: true }),
  cols.collateralType({ filter: true }),
  cols.pricingCurrency({ filter: true }),
  cols.billingCurrency({ filter: true }),
  cols.collateralCurrency({ filter: true }),
  cols.collateralMethod({ filter: true }),
  cols.dividendRequired({ filter: true }),
  cols.termType({ filter: true }),
  cols.termStartDate({ filter: true }),
  cols.termEndDate({ filter: true }),
  cols.balanceTarget({ filter: true }),
  cols.balanceTargetVariance({ filter: true }),
  cols.settlementSystem({ filter: true }),
  cols.profitCenter({ hiddenByDefault: true, filter: true }),
  cols.actions(
    canCreateOrEditAgreement.value ? 4 : 1,
    getAgreementActions({
      view: (agreement) => emit('view', agreement),
      duplicate: (agreement) => emit('duplicate', agreement),
      edit: (agreement) => emit('edit', agreement),
      close: (agreement) => emit('close', agreement),
      review: (agreement) => emit('review', agreement),
    })
  ),
]);

function getAgreementActions(availableActions: {
  view: (agreement: Data<Agreement>) => void;
  duplicate: (agreement: Data<Agreement>) => void;
  edit: (agreement: Data<Agreement>) => void;
  close: (agreement: Data<Agreement>) => void;
  review: (agreement: Data<Agreement>) => void;
}) {
  return function (agreement: Data<Agreement>) {
    const actions: {
      view?: () => void;
      duplicate?: () => void;
      edit?: () => void;
      close?: () => void;
      review?: () => void;
    } = {};

    // broker admin can only view the agreements
    if (isBrokerAdmin.value) {
      return { ...actions, view: () => availableActions.view(agreement) };
    }

    const canEditAgreement = useHasPermission(Permission.AGREEMENT_EDIT);
    switch (agreement.status) {
      case AgreementStatus.DRAFT: {
        const isIncoming = useIsIncoming(agreement);
        if (isIncoming.value) {
          actions.review = () => availableActions.view(agreement);
          return actions;
        }
        if (canEditAgreement.value) {
          actions.edit = () => availableActions.edit(agreement);
          actions.close = () => availableActions.close(agreement);
          actions.duplicate = () => availableActions.duplicate(agreement);
        }
        break;
      }
      case AgreementStatus.ACTIVE:
        if (canEditAgreement.value) {
          actions.edit = () => availableActions.edit(agreement);
          actions.close = () => availableActions.close(agreement);
          actions.duplicate = () => availableActions.duplicate(agreement);
        }
        break;
      case AgreementStatus.REJECTED:
      case AgreementStatus.CANCELED:
      case AgreementStatus.INACTIVE:
        if (canEditAgreement.value) {
          actions.duplicate = () => availableActions.duplicate(agreement);
        }
        break;
    }
    actions.view = () => availableActions.view(agreement);
    return actions;
  };
}

function getRowId(agreement: Data<Agreement>): string {
  return agreement.id;
}

const snackbarManager = useSnackbarManager();

const agreements = shallowRef([] as Array<Data<Agreement>>);

const canViewNonActiveAgreements = computed(
  () => isBrokerAdmin.value || canCreateOrEditAgreement.value
);

async function queryData(): Promise<Array<Data<Agreement>>> {
  try {
    const result = await agreementsService.queryAgreements(
      {
        statuses: canViewNonActiveAgreements.value ? [] : [AgreementStatus.ACTIVE],
        bookingAccounts: [],
        counterpartyBookingAccounts: [],
        counterpartyIds: [],
        tradeTypes: [],
        collateralCurrencies: [],
        deliveryTypes: [],
      },
      props.companyIdFilter
    );
    if (!result.success) {
      snackbarManager.error(i18n.t(result.error, result.details) as string);
      return Promise.resolve([]);
    }

    // bubble up reserved short names so we can do client-side unique checks (in addition to the server-side checks)
    populateReservedShortNames(result.data.agreements);

    return Promise.resolve(result.data.agreements);
  } catch (e) {
    logger.warn(e);

    return Promise.resolve([]);
  }
}

async function refreshData(): Promise<void> {
  agreements.value = await queryData();
}

onMounted(async () => {
  await refreshData();
});

// Refresh Data whenever side changes (or rights)
watchEffect(refreshData);

function populateReservedShortNames(agreements: Array<Data<Agreement>>) {
  emit(
    'update:reserved-short-names',
    new Set(
      agreements
        .filter(
          (agreement) =>
            [AgreementStatus.ACTIVE, AgreementStatus.DRAFT].includes(agreement.status) &&
            agreement.shortName
        )
        .map((agreement) => agreement.shortName.toUpperCase())
    )
  );
}
</script>

<template>
  <AgTableClient
    :column-defs="columnDefs"
    data-test-id="AgreementsTable"
    :get-row-id="getRowId"
    :page="1"
    :page-size="Infinity"
    :row-data="agreements"
    :sort="{ colId: 'id', sort: 'asc' }"
    table-id="AgreementsTable"
  />
</template>
